import { apiClient } from './api';
import {
  Batch,
  CreateBatchDTO,
  UpdateBatchDTO,
  Harvest,
  CreateHarvestDTO,
  JournalEntry,
  CreateJournalEntryDTO,
  Recipe,
  CreateRecipeDTO,
  OptimalEnvironment,
  DosingTable,
  CreateDosingTableDTO,
  UpdateDosingTableDTO,
  JournalStats,
  BatchWithFeatures,
  BatchFeatures,
} from '../types/growth';

export const GrowthService = {
  // Batches
  createBatch: async (data: CreateBatchDTO): Promise<Batch> => {
    return apiClient.post('/growth/batches', data);
  },

  updateBatch: async (id: string, data: UpdateBatchDTO): Promise<Batch> => {
    return apiClient.patch(`/growth/batches/${id}`, data);
  },

  getActiveBatch: async (deviceId: string): Promise<Batch | null> => {
    return apiClient.get('/growth/batches/active', { params: { deviceId } });
  },

  getOptimalEnvironment: async (deviceId: string): Promise<OptimalEnvironment | null> => {
    return apiClient.get('/growth/batches/optimal', { params: { deviceId } });
  },

  getDeviceBatches: async (deviceId: string): Promise<Batch[]> => {
    const result = await apiClient.get<any>(`/growth/batches/device/${deviceId}`);
    return result.data;
  },

  getBatch: async (id: string): Promise<Batch> => {
    return apiClient.get(`/growth/batches/${id}`);
  },

  getBatchPlants: async (batchId: string): Promise<any[]> => {
    const result = await apiClient.get<any>(`/growth/batches/${batchId}/plants`);
    return result.data;
  },

  createPlants: async (batchId: string, plants: any[]): Promise<any[]> => {
    return apiClient.post(`/growth/batches/${batchId}/plants`, { plants });
  },

  updatePlant: async (plantId: string, data: any): Promise<any> => {
    return apiClient.patch(`/growth/plants/${plantId}`, data);
  },

  deletePlant: async (plantId: string): Promise<void> => {
    await apiClient.delete(`/growth/plants/${plantId}`);
  },

  // Harvests
  recordHarvest: async (batchId: string, data: CreateHarvestDTO): Promise<Harvest> => {
    return apiClient.post(`/growth/batches/${batchId}/harvest`, data);
  },

  // Journal
  addJournalEntry: async (
    data: CreateJournalEntryDTO,
    coordinates?: { lat: number; lon: number },
  ): Promise<JournalEntry> => {
    return apiClient.post('/growth/journal', { ...data, coordinates });
  },

  /**
   * Pridat zaznam do vice vysadeb najednou (spolecne zaznamy)
   * Napriklad zalivka celeho zahonu kde je vice ruznych rostlin
   */
  addJournalEntryToMultipleBatches: async (
    batchIds: string[],
    data: Omit<CreateJournalEntryDTO, 'batch_id'>,
    coordinates?: { lat: number; lon: number },
  ): Promise<JournalEntry[]> => {
    return apiClient.post('/growth/journal/multiple', {
      batch_ids: batchIds,
      ...data,
      coordinates,
    });
  },

  /**
   * Pridat zaznam na zahon (branch) - automaticky propise do vsech aktivnich vysadeb
   */
  addJournalEntryToBranch: async (
    branchId: string,
    data: Omit<CreateJournalEntryDTO, 'batch_id'>,
    coordinates?: { lat: number; lon: number },
  ): Promise<{
    entries: JournalEntry[];
    batch_count: number;
    created_count: number;
    skipped_count: number;
    skipped_reason?: string;
    all_duplicates: boolean;
  }> => {
    return apiClient.post('/growth/journal/branch', {
      branch_id: branchId,
      ...data,
      coordinates,
    });
  },

  /**
   * Ziskat aktivni batche na danem branchi
   */
  getActiveBatchesByBranch: async (branchId: string): Promise<Batch[]> => {
    const result = await apiClient.get<any>(`/growth/branches/${branchId}/batches`);
    return result.data;
  },

  getBatchJournal: async (batchId: string): Promise<JournalEntry[]> => {
    const result = await apiClient.get<any>(`/growth/journal/batch/${batchId}`);
    return result.data;
  },

  /** Globalni zaznamy deniku v casovem rozmezi */
  getRecentJournal: async (from?: string, to?: string, limit?: number): Promise<JournalEntry[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (limit) params.append('limit', limit.toString());
    const result = await apiClient.get<any>(`/journal/recent?${params.toString()}`);
    return result.data;
  },

  /** Pocet zaznamu v deniku za posledni tyden */
  getJournalWeeklyCount: async (): Promise<number> => {
    const result = await apiClient.get<any>('/journal/weekly-count');
    return result.data.count;
  },

  // ============================================
  // ML-FIRST ENDPOINTS (FAZE 1)
  // ============================================

  /**
   * Ziskat ML statistiky pro journal batch
   */
  getJournalStats: async (batchId: string): Promise<JournalStats> => {
    return apiClient.get(`/journal/batch/${batchId}/stats`);
  },

  /**
   * Ziskat batch s ML features
   */
  getBatchWithFeatures: async (batchId: string): Promise<BatchWithFeatures> => {
    return apiClient.get(`/batches/${batchId}/features`);
  },

  /**
   * Spustit vypocet ML features pro batch (admin)
   */
  computeBatchFeatures: async (batchId: string): Promise<BatchFeatures> => {
    return apiClient.post(`/batches/${batchId}/compute-features`);
  },

  /**
   * Ziskat vsechny sklizne pro batch
   */
  getBatchHarvests: async (batchId: string): Promise<Harvest[]> => {
    const result = await apiClient.get<any>(`/batches/${batchId}/harvests`);
    return result.data || result;
  },

  // Recipes
  getRecipes: async (): Promise<Recipe[]> => {
    const result = await apiClient.get<any>('/growth/recipes');
    return result.data;
  },

  createRecipe: async (data: CreateRecipeDTO): Promise<Recipe> => {
    return apiClient.post('/growth/recipes', data);
  },

  updateRecipe: async (id: string, data: Partial<CreateRecipeDTO>): Promise<Recipe> => {
    return apiClient.put(`/growth/recipes/${id}`, data);
  },

  deleteRecipe: async (id: string): Promise<void> => {
    await apiClient.delete(`/growth/recipes/${id}`);
  },

  // Dosing Tables (Presets)
  getDosingTables: async (): Promise<DosingTable[]> => {
    return apiClient.get('/dosing-tables');
  },

  createDosingTable: async (data: CreateDosingTableDTO): Promise<DosingTable> => {
    return apiClient.post('/dosing-tables', data);
  },

  updateDosingTable: async (id: string, data: UpdateDosingTableDTO): Promise<DosingTable> => {
    return apiClient.put(`/dosing-tables/${id}`, data);
  },

  deleteDosingTable: async (id: string): Promise<void> => {
    await apiClient.delete(`/dosing-tables/${id}`);
  },

  uploadPhoto: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('photo', file);
    const result = await apiClient.post<any>('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return result.url;
  },
};
